// AbShell — Rahmen der Auftragsbestaetigungs-Arbeitsoberflaeche mit Sidebar-Navigation
// (Vorbild: OC-Pilot App.tsx-Shell + gineex components/LagerShell.js). Ersetzt den bisherigen
// inline-Header von AuftragsbestaetigungView.js; die eigentliche View bleibt duenner Host
// (Kompositionswurzel-Konvention). Subview-Routing laeuft rein ueber lokalen State (KEIN
// react-router, Monorepo-Konvention: currentView-Switch).
//
// Subviews: dashboard (Start) | documents | detail(id) | rulesets[admin] | monitoring[admin] |
// mail(id) | myreports | mapping(documentId)[admin].
//
// UI-Paritaet Welle 2 (Agent FE-3): 'mapping' ist NUR additiv angebunden — kein eigener Sidebar-
// Punkt (der visuelle Feld-Mapping-Editor oeffnet ausschliesslich aus dem Dokument-Detail-Button
// "PDF & Feld-Mapping" [onOpenMapping] bzw. aus der Regelset-Verwaltung selbst [AbRulesetAdmin
// rendert AbMappingEditor inline, analog zu AbRulesetEditor] — s. mapping/AbMappingEditor.js).

import React, { useCallback, useState } from 'react';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';
import AbSidebar from './AbSidebar';
import AbSyncBar from './AbSyncBar';
import AbNotificationBell from './AbNotificationBell';
import AbReportsFlag from './AbReportsFlag';
import AbDashboard from '../AbDashboard';
import AbDocumentsPage from '../AbDocumentsPage';
import AbDocumentDetail from '../AbDocumentDetail';
import AbRulesetAdmin from '../AbRulesetAdmin';
import AbMonitoring from '../AbMonitoring';
import AbMailDetail from '../AbMailDetail';
import AbMyReports from '../AbMyReports';
import AbMappingEditor from '../../mapping/AbMappingEditor';

const AbShell = ({ user, isDarkMode, t, onBack, showSuccess, showError }) => {
  const [view, setView] = useState({ name: 'dashboard' });
  // Zaehlt bei jedem Sub-View-Wechsel hoch -> AbReportsFlag aktualisiert die Badge-Zahl
  // (fängt z. B. eine gerade abgesendete Meldung beim Rücksprung aus der Detailansicht ein).
  const [navTick, setNavTick] = useState(0);

  const isAdmin = user?.role === 'admin';

  const bump = () => setNavTick((n) => n + 1);
  const goDashboard = useCallback(() => { setView({ name: 'dashboard' }); bump(); }, []);
  const goDocuments = useCallback((filter) => { setView({ name: 'documents', filter: filter || null }); bump(); }, []);
  const openDocument = useCallback((id) => { setView({ name: 'detail', documentId: id }); bump(); }, []);
  const openMail = useCallback((id) => { setView({ name: 'mail', mailId: id }); bump(); }, []);
  // Mapping-Editor ist ADMIN-only (AbMappingEditor selbst zeigt Nicht-Admins nur einen Hinweis) —
  // der Sprung dorthin wird trotzdem defensiv auch hier schon gegated (kein toter Klick).
  const openMapping = useCallback((id) => { if (isAdmin) { setView({ name: 'mapping', documentId: id }); bump(); } }, [isAdmin]);
  const goRulesets = useCallback(() => { if (isAdmin) { setView({ name: 'rulesets' }); bump(); } }, [isAdmin]);
  const goMonitoring = useCallback(() => { if (isAdmin) { setView({ name: 'monitoring' }); bump(); } }, [isAdmin]);
  const goMyReports = useCallback(() => { setView({ name: 'myreports' }); bump(); }, []);

  const onSidebarSelect = (id) => {
    if (id === 'dashboard') goDashboard();
    else if (id === 'documents') goDocuments();
    else if (id === 'rulesets') goRulesets();
    else if (id === 'monitoring') goMonitoring();
  };

  // Aktiver Sidebar-Eintrag: detail()/mapping() gehoeren visuell zu "documents", mail()/myreports()
  // zu "dashboard" (mapping hat bewusst KEINEN eigenen Sidebar-Punkt, s. Kopfkommentar).
  const sidebarActive = (view.name === 'detail' || view.name === 'mapping') ? 'documents'
    : (view.name === 'mail' || view.name === 'myreports') ? 'dashboard'
      : view.name;

  const bgCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
    : 'bg-[#F8FAFC]';
  const headerBg = isDarkMode
    ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
    : 'bg-white border-b border-slate-200';
  const iconBoxCls = isDarkMode
    ? 'bg-gradient-to-br from-indigo-500/70 via-indigo-600/60 to-indigo-700/70 shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/20'
    : 'bg-indigo-100';
  const titleCls = isDarkMode
    ? 'bg-gradient-to-r from-white via-indigo-200 to-white bg-clip-text text-transparent text-xl font-bold'
    : 'text-slate-900 text-xl font-bold';
  const subtitleCls = isDarkMode ? 'text-white/50 text-xs mt-0.5' : 'text-slate-500 text-xs mt-0.5';
  const backBtnCls = isDarkMode
    ? 'p-2 rounded-xl transition-colors hover:bg-white/10 text-white/70 hover:text-white flex-shrink-0'
    : 'p-2 rounded-xl transition-colors hover:bg-slate-100 text-slate-500 hover:text-slate-700 flex-shrink-0';
  const contentTopbarCls = isDarkMode
    ? 'border-b border-white/8 bg-white/[0.02]'
    : 'border-b border-slate-200 bg-white';

  const renderContent = () => {
    switch (view.name) {
      case 'documents':
        return (
          <AbDocumentsPage
            user={user} isDarkMode={isDarkMode} t={t}
            onOpenDocument={openDocument} showSuccess={showSuccess} showError={showError}
            initialFilter={view.filter}
          />
        );
      case 'detail':
        return (
          <AbDocumentDetail
            user={user} isDarkMode={isDarkMode} t={t} documentId={view.documentId}
            onBack={() => goDocuments()} showSuccess={showSuccess} showError={showError}
            onOpenDocument={openDocument} onOpenMail={openMail}
            onOpenMapping={isAdmin ? openMapping : undefined}
          />
        );
      case 'mapping':
        return (
          <AbMappingEditor
            user={user} isDarkMode={isDarkMode} t={t} documentId={view.documentId}
            onBack={() => openDocument(view.documentId)} onOpenDocument={openDocument}
            showSuccess={showSuccess} showError={showError}
          />
        );
      case 'rulesets':
        return (
          <AbRulesetAdmin
            user={user} isDarkMode={isDarkMode} t={t}
            onBack={goDashboard} onOpenDocument={openDocument} showSuccess={showSuccess} showError={showError}
          />
        );
      case 'monitoring':
        return (
          <AbMonitoring
            user={user} isDarkMode={isDarkMode} t={t} onOpenDocument={openDocument}
            showSuccess={showSuccess} showError={showError}
          />
        );
      case 'mail':
        return (
          <AbMailDetail
            user={user} isDarkMode={isDarkMode} t={t} mailId={view.mailId}
            onBack={goDashboard} onOpenDocument={openDocument} showError={showError}
          />
        );
      case 'myreports':
        return (
          <AbMyReports
            user={user} isDarkMode={isDarkMode} t={t}
            onOpenDocument={openDocument} onBack={goDashboard} showError={showError}
          />
        );
      case 'dashboard':
      default:
        return (
          <AbDashboard
            user={user} isDarkMode={isDarkMode} t={t}
            onOpenDocument={openDocument} onOpenMail={openMail} onGoDocuments={goDocuments}
            showSuccess={showSuccess} showError={showError}
          />
        );
    }
  };

  return (
    <div className={`flex-1 min-w-0 min-h-screen flex flex-col ${bgCls}`}>
      {/* ---- Modul-Header (schmal: Zurueck + Titel), Muster LagerShell ---- */}
      <div className="relative flex-shrink-0">
        <div className={`absolute inset-0 ${headerBg}`} />
        <div className="relative max-w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            {typeof onBack === 'function' && (
              <button type="button" onClick={onBack} className={backBtnCls} aria-label={t('common.back')} title={t('common.back')}>
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBoxCls}`}>
                <ClipboardCheck className={`w-4 h-4 ${isDarkMode ? 'text-white/90' : 'text-indigo-600'}`} />
              </div>
              <div>
                <h1 className={titleCls}>{t('ab.title')}</h1>
                <p className={subtitleCls}>{t('ab.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Body: Sidebar + Inhalt ---- */}
      <div className="flex flex-1 min-h-0">
        <AbSidebar isDarkMode={isDarkMode} t={t} active={sidebarActive} onSelect={onSidebarSelect} isAdmin={isAdmin} />

        <div className="flex-1 flex flex-col min-w-0">
          {/* ---- Inhalts-Kopfleiste: Sync + Melden + Benachrichtigungen (Vorbild OC-Pilot topbar) ---- */}
          <div className={`flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-3 flex-wrap ${contentTopbarCls}`}>
            {isAdmin && <AbSyncBar user={user} isDarkMode={isDarkMode} t={t} showSuccess={showSuccess} showError={showError} />}
            <div className="flex-1" />
            <AbReportsFlag
              user={user} isDarkMode={isDarkMode} t={t}
              active={view.name === 'myreports'} onOpen={goMyReports} refreshKey={navTick}
            />
            <AbNotificationBell user={user} isDarkMode={isDarkMode} t={t} onOpenDocument={openDocument} />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbShell;
